// One chat message — ports ui.js _createMessageHTML + renderMessageContent.
// Text content runs through formatMessage (escaped + markdown subset) and is
// injected as HTML. Media types render <img>/<video>/<audio>; images open the
// lightbox. Assistant insight messages with [CTA_SESSION] show a session CTA.

import { useState } from 'react'
import { formatMessage, CTA_SESSION_MARKER } from '../../lib/formatMessage'
import { useLightboxStore } from '../../store/lightboxStore'
import { useAuthStore } from '../../store/authStore'
import { isNative, mediaSrc } from '../../lib/platform'
import { useVideoProgress } from '../../features/video-progress'
import { scaleKey } from '../../lib/scaleKey'
import Reactions from './Reactions'
import ChatPaywall from './ChatPaywall'
import ScaleButtons from './ScaleButtons'
import type { ChatMessage } from '../../api/chat'
import styles from './Message.module.css'

type MediaType = 'image' | 'video' | 'audio'

interface MediaProps {
  type: MediaType
  src: string
  onImageClick?: (src: string) => void
  onMediaLoad?: () => void
}

interface TextContentProps {
  content: string
  isUser: boolean
  onInsightCta?: () => void
}

interface MessageProps {
  msg: ChatMessage
  index: number
  onInsightCta?: () => void
  onMediaLoad?: () => void
  /** #4497: false when a later user message exists — past scale keyboards
   * stop rendering (the backend retired them for TG; on PWA this gate is
   * the sweep). Defaults true for callers that don't track the list. */
  scaleActive?: boolean
}

// Media with an onError fallback — mirrors the original which swapped broken
// media for a "Не удалось загрузить …" message (ui.js:172,180,192) instead of
// leaving the browser's broken-media icon.
function Media({ type, src, onImageClick, onMediaLoad }: MediaProps) {
  const [failed, setFailed] = useState(false)
  const userId = useAuthStore((state) => state.userId)
  const videoProgress = useVideoProgress({
    enabled: isNative && type === 'video',
    userId,
    src,
    onMediaLoad,
  })
  const LABEL = { image: 'изображение', video: 'видео', audio: 'аудио' }
  const handleImageClick = () => onImageClick?.(src)
  const handleMediaError = () => setFailed(true)

  if (failed) return <div className={styles.mediaError}>Не удалось загрузить {LABEL[type]}</div>
  if (type === 'image')
    return (
      <img
        className={styles.image}
        src={src}
        alt=""
        loading="lazy"
        onClick={handleImageClick}
        onLoad={onMediaLoad}
        onError={handleMediaError}
      />
    )
  if (type === 'video')
    // onLoadedMetadata (not just onLoadedData): the metadata load is what resizes
    // the <video> from its default 300×150 to the real aspect ratio, shifting the
    // transcript below — and on iOS Safari with preload=metadata, loadeddata often
    // never fires, so metadata is the only re-scroll trigger.
    return (
      <video
        className={styles.video}
        src={src}
        controls
        preload="metadata"
        onLoadedMetadata={videoProgress.handleLoadedMetadata}
        onLoadedData={onMediaLoad}
        onTimeUpdate={videoProgress.handleTimeUpdate}
        onPause={videoProgress.handlePause}
        onEnded={videoProgress.handleEnded}
        onError={handleMediaError}
      />
    )
  // Voice message — surface card + 🎵 icon so it stands out from the gray chat
  // background (ports the original .audio-player treatment).
  return (
    <div className={styles.audioPlayer}>
      <span className={styles.audioIcon} aria-hidden>🎵</span>
      <audio className={styles.audio} src={src} controls preload="metadata" onError={handleMediaError} />
    </div>
  )
}

function TextContent({ content, isUser, onInsightCta }: TextContentProps) {
  const hasCta = !isUser && content.includes(CTA_SESSION_MARKER)
  if (hasCta) {
    const [before] = content.split(CTA_SESSION_MARKER)
    return (
      <>
        <div className={styles.text} dangerouslySetInnerHTML={{ __html: formatMessage(before) }} />
        <div className={styles.ctaBlock}>
          <p>Месяц позади. Хотите вместе с психологом наметить следующие шаги и разобрать, что получилось?</p>
          <button className={styles.ctaBtn} onClick={onInsightCta}>Записаться на сессию →</button>
        </div>
      </>
    )
  }
  return <div className={styles.text} dangerouslySetInnerHTML={{ __html: formatMessage(content) }} />
}

export default function Message({ msg, index, onInsightCta, onMediaLoad, scaleActive = true }: MessageProps) {
  const openLightbox = useLightboxStore((s) => s.open)
  const isUser = msg.role === 'user'
  // mediaSrc: on native the backend's root-relative /api/v1/media/... would
  // resolve against the app bundle instead of the backend. No-op on web.
  const src = mediaSrc(msg.mediaUrl || msg.content)

  const isPaywall = msg.contentType === 'paywall'

  let body
  switch (msg.contentType) {
    case 'image':
    case 'video':
      body = (
        <div className={styles.media}>
          <Media
            type={msg.contentType}
            src={src}
            onImageClick={openLightbox}
            onMediaLoad={onMediaLoad}
          />
        </div>
      )
      break
    case 'audio':
      // Audio is its own surface card (with shadow), so it skips the overflow:
      // hidden .media wrapper that would clip the shadow.
      body = <Media type="audio" src={src} onMediaLoad={onMediaLoad} />
      break
    case 'paywall':
      body = (
        <>
          <div className={`${styles.text} ${styles.paywallText}`}>{msg.content}</div>
          <ChatPaywall />
        </>
      )
      break
    default:
      body = <TextContent content={msg.content} isUser={isUser} onInsightCta={onInsightCta} />
  }

  return (
    <div className={`${styles.message} ${isUser ? styles.user : styles.assistant}`}>
      <div className={styles.avatar}>
        {isUser ? '👤' : <img src="/assets/app-icon.png" alt="" className={styles.avatarImg} />}
      </div>
      <div className={styles.content}>
        {body}
        {!isUser && !isPaywall && scaleActive && (
          <ScaleButtons
            points={msg.metadata?.engine_scale?.points || []}
            msgKey={msg.metadata?.engine_scale?.id ?? scaleKey(msg.content)}
          />
        )}
        {!isUser && !isPaywall && <Reactions index={index} reactions={msg.reactions} />}
      </div>
    </div>
  )
}
